import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Trash2 } from 'lucide-react';
import { usePortalAppearance } from './portalAppearanceContext';
import { getPortalPbToken, getPortalPocketBaseUrl } from '../../constants';
import {
  listKanzleiSeatsForAktePicker,
  listMyPortalAkteMembers,
  listPortalAktenForCustomer,
} from './portalAkten';
import { loadCalendarEventsFromPb } from './calendar/portalCalendarPersist';
import { listPortalDocumentShares } from './portalDocumentShares';
import { fetchPortalProtokollById } from './portalDocuments';
import { readElementRoomsResponse } from './elementSeatInvite';
import { analysisBellEntries } from './portalAnalysisSurface';
import { glockePanelColors, glockeSprungFor, publishGlockeSprung } from './portalGlockeSprung';
import { useOptionalPortalBackgroundJobs } from './portalBackgroundJobsContext';
import {
  analysisGlockeId,
  deleteSitzGlockeItem,
  filterSitzGlockeEventsForSeat,
  loadSitzGlockeState,
  markSitzGlockeItemRead,
  markSitzGlockeItemUnread,
  mergeSitzGlockeFromLive,
  saveSitzGlockeState,
  sitzGlockeActorFromSeat,
  sitzGlockeActorSentence,
  sitzGlockeAttentionCount,
  sitzGlockeKindLabel,
  sitzGlockeSourceLabel,
  SITZ_GLOCKE_CHANGED_EVENT,
  SITZ_GLOCKE_OPEN_EVENT,
  type SitzGlockeEvent,
  type SitzGlockeLiveDoc,
  type SitzGlockeLiveElement,
  type SitzGlockeState,
} from './sitzGlockeStore';

export type PortalSitzGlockeProps = {
  customerId: string;
  kanzleiId?: string;
  /** Desktop: in der Sidebar. Mobile: in der Leiste. */
  placement?: 'sidebar' | 'mobile';
};

const PORTAL_DOCUMENTS_COLLECTION = 'portal_documents';

function GlockeRowActions({
  itemId,
  tone,
  onDelete,
  onUnread,
}: {
  itemId: string;
  tone: boolean;
  onDelete: (itemId: string) => void;
  onUnread: (itemId: string) => void;
}) {
  const stop = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const btn = tone
    ? 'portal-hell-hover rounded p-1'
    : 'rounded p-1 text-slate-400 hover:bg-white/[0.06] hover:text-slate-100';
  return (
    <div className="flex shrink-0 flex-col py-1 pr-1">
      <button
        type="button"
        data-testid="portal-sitz-glocke-loeschen"
        className={btn}
        aria-label="Löschen"
        title="Löschen"
        onMouseDown={stop}
        onClick={(event) => {
          stop(event);
          onDelete(itemId);
        }}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
      <button
        type="button"
        data-testid="portal-sitz-glocke-ungelesen"
        className={`${btn} text-[10px] font-medium`}
        aria-label="Als ungelesen markieren"
        title="Als ungelesen markieren"
        onMouseDown={stop}
        onClick={(event) => {
          stop(event);
          onUnread(itemId);
        }}
      >
        Ungelesen
      </button>
    </div>
  );
}

export const PortalSitzGlocke: React.FC<PortalSitzGlockeProps> = ({
  customerId,
  kanzleiId = '',
  placement = 'sidebar',
}) => {
  const light = usePortalAppearance() === 'light';
  const jobs = useOptionalPortalBackgroundJobs();
  const analysisRows = analysisBellEntries(jobs?.analysisViews ?? []);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SitzGlockeState>(() => loadSitzGlockeState(customerId));
  const rootRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    const seatId = customerId.trim();
    const kid = kanzleiId.trim();
    if (!seatId || !kid) return;
    const token = getPortalPbToken()?.trim() || '';
    if (!token) return;
    const prev = loadSitzGlockeState(seatId);
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    try {
      const [members, akten, termine, seats, shares, roomsRes] = await Promise.all([
        listMyPortalAkteMembers(token, seatId, kid),
        listPortalAktenForCustomer(seatId, token, kid),
        loadCalendarEventsFromPb({ customerId: seatId, kanzleiId: kid, authToken: token }).catch(() => []),
        listKanzleiSeatsForAktePicker(token, kid).catch(() => []),
        listPortalDocumentShares(token).catch(() => []),
        pbBase
          ? fetch(`${pbBase}/api/arkorbit/element-rooms`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then(async (res) => (res.ok ? ((await res.json().catch(() => ({}))) as unknown) : null))
              .catch(() => null)
          : Promise.resolve(null),
      ]);
      if (members === null) return;
      const seatById = new Map(seats.map((row) => [row.id, row]));
      const titleByAkte = new Map(akten.map((row) => [row.id, row.title || 'Akte']));
      const myShares = shares.filter((row) => row.customer_id === seatId && row.kanzlei_id === kid);
      const docMetas = await Promise.all(
        myShares.slice(0, 40).map(async (share) => {
          const doc = await fetchPortalProtokollById(
            pbBase,
            PORTAL_DOCUMENTS_COLLECTION,
            share.document_id,
            token,
          ).catch(() => null);
          return { share, doc };
        }),
      );
      const docs: SitzGlockeLiveDoc[] = [];
      for (const row of docMetas) {
        const ownerId = (row.doc?.customer_id || '').trim();
        const ownerSeat = ownerId ? seatById.get(ownerId) : undefined;
        const actor = sitzGlockeActorFromSeat(ownerSeat || null);
        docs.push({
          documentId: row.share.document_id,
          shareId: row.share.id,
          title: row.doc?.filename || 'Dokument',
          seatId,
          ownerId: ownerId || undefined,
          actorName: actor?.name,
          actorRole: actor?.role,
        });
      }

      const rooms = roomsRes ? readElementRoomsResponse(roomsRes) : [];
      const elements: SitzGlockeLiveElement[] = [];
      for (const room of rooms) {
        const roomSeats = Array.isArray(room.seat_ids) ? room.seat_ids.map((x) => String(x || '').trim()) : [];
        if (!roomSeats.includes(seatId)) continue;
        elements.push({
          roomId: room.id,
          title: room.name || 'Element',
          seatId,
        });
      }

      const next = mergeSitzGlockeFromLive({
        seatId,
        prev,
        members: members.map((row) => ({
          recordId: row.id,
          akteId: row.akte_id,
          title: titleByAkte.get(row.akte_id) || 'Akte',
          seatId: row.customer_id,
        })),
        termine: termine.map((row) => {
          const ownerSeat = seatById.get(row.customerId);
          const actor = sitzGlockeActorFromSeat(ownerSeat || null);
          return {
            id: row.id,
            title: row.title,
            ownerId: row.customerId,
            attendeeIds: row.attendeeIds || [],
            actorName: actor?.name,
            actorRole: actor?.role,
          };
        }),
        docs,
        elements,
      });
      saveSitzGlockeState(next);
      setState(next);
    } catch {
      /* Glocke bleibt mit letztem Stand. */
    }
  }, [customerId, kanzleiId]);

  useEffect(() => {
    setState(loadSitzGlockeState(customerId));
    void refresh();
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => {
      void refresh();
    }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [customerId, refresh]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onChanged = () => setState(loadSitzGlockeState(customerId));
    window.addEventListener(SITZ_GLOCKE_OPEN_EVENT, onOpen);
    window.addEventListener(SITZ_GLOCKE_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(SITZ_GLOCKE_OPEN_EVENT, onOpen);
      window.removeEventListener(SITZ_GLOCKE_CHANGED_EVENT, onChanged);
    };
  }, [customerId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const hidden = new Set(state.hiddenIds || []);
  const attention = sitzGlockeAttentionCount(
    state,
    analysisRows.map((row) => row.requestId),
  );
  const rows: SitzGlockeEvent[] = filterSitzGlockeEventsForSeat(state.events, customerId).filter(
    (row) => !hidden.has(row.id),
  );
  const visibleAnalysis = analysisRows.filter((row) => !hidden.has(analysisGlockeId(row.requestId)));
  const tone = glockePanelColors(light ? 'light' : 'dark');

  const persist = (next: SitzGlockeState) => {
    saveSitzGlockeState(next);
    setState(next);
  };

  const jumpTo = (source: string, refId: string, itemId: string) => {
    const sprung = glockeSprungFor(source, refId);
    if (!sprung || !itemId) return;
    persist(markSitzGlockeItemRead(loadSitzGlockeState(customerId), itemId));
    publishGlockeSprung(sprung);
    setOpen(false);
  };

  const openAnalysis = (requestId: string) => {
    jumpTo('analyse', requestId, analysisGlockeId(requestId));
    jobs?.setOpenJobId(requestId);
  };

  const removeItem = (itemId: string) => {
    persist(deleteSitzGlockeItem(loadSitzGlockeState(customerId), itemId));
  };

  const markUnread = (itemId: string) => {
    persist(markSitzGlockeItemUnread(loadSitzGlockeState(customerId), itemId));
  };

  const toggle = () => {
    setOpen((wasOpen) => !wasOpen);
  };

  return (
    <div
      ref={rootRef}
      className={placement === 'mobile' ? 'relative shrink-0' : 'relative ml-auto shrink-0'}
      data-testid="portal-sitz-glocke"
    >
      <button
        type="button"
        data-testid="portal-sitz-glocke-btn"
        aria-label={attention > 0 ? `Benachrichtigungen, ${attention} ungelesen` : 'Benachrichtigungen'}
        aria-expanded={open}
        onClick={toggle}
        className={`portal-hell-hover relative rounded-lg p-2 ${light ? 'text-[#1c1915] hover:bg-[#f3efe8]' : 'text-slate-300 hover:bg-white/[0.06]'} ${
          attention > 0 ? (light ? 'text-[#5b4678]' : 'text-violet-200') : ''
        }`}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {attention > 0 ? (
          <span
            data-testid="portal-sitz-glocke-badge"
            className="absolute -right-0.5 -top-0.5 min-w-[1rem] rounded-full bg-violet-500/90 px-1 text-[9px] font-semibold leading-4 text-white"
          >
            {attention > 9 ? '9+' : attention}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          data-testid="portal-sitz-glocke-panel"
          data-glocke-tone={tone ? 'light' : 'dark'}
          role="dialog"
          aria-label="Benachrichtigungen dieses Sitzes"
          className={`absolute z-[60] w-[min(19.8rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border shadow-xl ${
            tone ? '' : 'border-white/[0.08] bg-slate-950/95 backdrop-blur'
          } ${placement === 'mobile' ? 'right-0 top-full mt-1' : 'left-0 top-full mt-1'}`}
          style={
            tone
              ? { backgroundColor: tone.surface, color: tone.text, borderColor: tone.accent }
              : undefined
          }
        >
          <div className={`border-b px-3 py-2 ${tone ? '' : 'border-white/[0.06]'}`} style={tone ? { borderColor: tone.accent } : undefined}>
            <p className={`m-0 text-[11px] font-semibold ${tone ? '' : 'text-slate-200'}`}>Für diesen Sitz</p>
            <p className={`m-0 mt-0.5 text-[10px] ${tone ? '' : 'text-slate-500'}`}>Nur echte Vorgänge · keine Beispiele</p>
          </div>
          {visibleAnalysis.length > 0 ? (
            <ul className="m-0 list-none space-y-0 p-0" data-testid="portal-sitz-glocke-analysen">
              {visibleAnalysis.map((row) => {
                const itemId = analysisGlockeId(row.requestId);
                return (
                  <li key={row.requestId} className={`flex items-start border-t first:border-t-0 ${tone ? '' : 'border-white/[0.04]'}`}>
                    <button
                      type="button"
                      data-testid="portal-sitz-glocke-analyse"
                      className={`block min-w-0 flex-1 px-3 py-2 text-left ${tone ? 'portal-hell-hover' : 'hover:bg-white/[0.04]'}`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openAnalysis(row.requestId);
                      }}
                    >
                      <p
                        className={`m-0 text-[10px] font-semibold uppercase tracking-wide ${tone ? '' : 'text-violet-300/90'}`}
                        style={tone ? { color: tone.accent } : undefined}
                      >
                        Analyse
                      </p>
                      <p className={`m-0 mt-0.5 text-[11px] ${tone ? '' : 'text-slate-200'}`}>{row.line}</p>
                    </button>
                    <GlockeRowActions itemId={itemId} tone={Boolean(tone)} onDelete={removeItem} onUnread={markUnread} />
                  </li>
                );
              })}
            </ul>
          ) : null}
          {rows.length === 0 && visibleAnalysis.length === 0 ? (
            <p
              data-testid="portal-sitz-glocke-empty"
              className={`m-0 px-3 py-4 text-center text-[11px] ${tone ? '' : 'text-slate-500'}`}
            >
              Nichts Neues
            </p>
          ) : rows.length === 0 ? null : (
            <ul className="m-0 max-h-64 list-none space-y-0 overflow-y-auto p-0">
              {rows.map((row) => {
                const actorLine = sitzGlockeActorSentence(row.kind, row.actorName, row.actorRole);
                const read = state.seenIds.includes(row.id);
                return (
                  <li
                    key={row.id}
                    data-testid={`portal-sitz-glocke-row-${row.kind}`}
                    data-glocke-kind={row.kind}
                    data-glocke-source={row.source}
                    data-glocke-actor={row.actorName || ''}
                    data-glocke-read={read ? '1' : '0'}
                    className={`flex items-start border-t first:border-t-0 ${tone ? '' : 'border-white/[0.04]'}`}
                  >
                    <button
                      type="button"
                      data-testid="portal-sitz-glocke-sprung"
                      className={`block min-w-0 flex-1 px-3 py-2 text-left ${tone ? 'portal-hell-hover' : 'hover:bg-white/[0.04]'}`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        jumpTo(row.source, row.refId, row.id);
                      }}
                    >
                      <p
                        className={`m-0 text-[10px] font-semibold uppercase tracking-wide ${tone ? '' : 'text-violet-300/90'}`}
                        style={tone ? { color: tone.accent } : undefined}
                      >
                        {sitzGlockeKindLabel(row.kind)}
                      </p>
                      {actorLine ? (
                        <p className={`m-0 mt-0.5 text-[11px] ${tone ? '' : 'text-slate-200'}`} data-testid="portal-sitz-glocke-actor">
                          {actorLine}
                        </p>
                      ) : null}
                      <p className={`m-0 mt-0.5 text-[11px] ${tone ? '' : 'text-slate-400'}`}>
                        {sitzGlockeSourceLabel(row.source)}
                        {row.title ? ` · ${row.title}` : ''}
                      </p>
                    </button>
                    <GlockeRowActions itemId={row.id} tone={Boolean(tone)} onDelete={removeItem} onUnread={markUnread} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};
